/**
 * API Configuration
 * Centralized configuration for all microservice endpoints
 *
 * Supports 3 backend modes:
 *   - local          : Vite dev proxy → Docker Compose localhost services
 *   - cloud-gateway  : Direct HTTPS → API Gateway → K8s microservices (Option 1)
 *   - cloud-direct   : Direct HTTPS → per-service subdomains (Option 2)
 */

export type BackendMode = 'local' | 'cloud-gateway' | 'cloud-direct'

export const BACKEND_MODE: BackendMode =
  (import.meta.env.VITE_BACKEND_MODE as BackendMode) || 'local'

// ── Cloud-direct service URL map ──────────────────────────────────────────────
const CLOUD_SERVICE_URLS = {
  governance: import.meta.env.VITE_GOVERNANCE_SERVICE_URL || '',
  user: import.meta.env.VITE_USER_SERVICE_URL || '',
  tenant: import.meta.env.VITE_TENANT_SERVICE_URL || '',
  billing: import.meta.env.VITE_BILLING_SERVICE_URL || '',
  assessment: import.meta.env.VITE_ASSESSMENT_SERVICE_URL || '',
  risk: import.meta.env.VITE_RISK_SERVICE_URL || '',
  compliance: import.meta.env.VITE_COMPLIANCE_SERVICE_URL || '',
  audit: import.meta.env.VITE_AUDIT_SERVICE_URL || '',
  dashboard: import.meta.env.VITE_DASHBOARD_SERVICE_URL || '',
  reporting: import.meta.env.VITE_REPORTING_SERVICE_URL || '',
  notification: import.meta.env.VITE_NOTIFICATION_SERVICE_URL || '',
  strategy: import.meta.env.VITE_STRATEGY_SERVICE_URL || '',
  integration: import.meta.env.VITE_INTEGRATION_SERVICE_URL || '',
  aiOrchestrator: import.meta.env.VITE_AI_ORCHESTRATOR_SERVICE_URL || '',
  evidence: import.meta.env.VITE_EVIDENCE_SERVICE_URL || '',
  workspace: import.meta.env.VITE_WORKSPACE_SERVICE_URL || '',
  datapro: import.meta.env.VITE_DATAPRO_SERVICE_URL || '',
  settings: import.meta.env.VITE_SETTINGS_SERVICE_URL || '',
  featureEngine: import.meta.env.VITE_FEATURE_ENGINE_URL || '',
  clientManagement: import.meta.env.VITE_CLIENT_MGMT_URL || '',
  simulation: import.meta.env.VITE_SIMULATION_SERVICE_URL || '',
  governanceDocument: import.meta.env.VITE_GOVERNANCE_DOCUMENT_SERVICE_URL || '',
  // Service Evaluation & 360° (evaluation-service, port 5142). Only used in
  // cloud-direct mode; cloud-gateway routes /evaluation/* via VITE_API_BASE_URL.
  evaluation: import.meta.env.VITE_EVALUATION_SERVICE_URL || '',
  // RAG / semantic-search knowledge-service (port 5122). In cloud-gateway mode
  // (the deployed portal) /knowledge/* is routed by nginx; this entry only
  // matters for cloud-direct mode. Falls back to governance base if unset.
  knowledge: import.meta.env.VITE_KNOWLEDGE_SERVICE_URL || import.meta.env.VITE_GOVERNANCE_SERVICE_URL || '',
} as const

// ── Path → service mapping for cloud-direct mode ─────────────────────────────
// Maps URL path prefixes to their target cloud service
const PATH_SERVICE_MAP: Array<[RegExp, keyof typeof CLOUD_SERVICE_URLS]> = [
  // DataPRO routes to dedicated datapro-service (port 5121)
  [/^\/data-pro/, 'datapro'],
  // All other PRO modules route to governance-service
  [/^\/(governance-pro|security-pro|risk-pro|ai-ia-pro|compliance-pro|change-pro|vendor-pro|dashboard-pro|ethics-pro|maturity-pro|strategy-pro|audit-pro|workspace-pro|business-model-pro)/, 'governance'],
  // Simulation-service (cloud-direct): bare /simulations/* → simulation-service
  // (router prefix /api/v1/simulations). Was /^\/simulation-service/ back when the
  // endpoint constants carried the service name inline; now the constants are bare
  // /simulations/* (they double-stacked /api/v1 in cloud-gateway mode otherwise).
  [/^\/simulations/, 'simulation'],
  [/^\/governance-documents/, 'governanceDocument'],
  // Service Evaluation & 360° — cross-actor evaluation engine (evaluation-service).
  [/^\/evaluation/, 'evaluation'],
  [/^\/controls/, 'governance'],
  // Individual service routes
  [/^\/users/, 'user'],
  [/^\/tenants/, 'tenant'],
  [/^\/billing/, 'billing'],
  [/^\/assessments/, 'assessment'],
  // Framework catalog (CreateAssessment wizard step 0 -> /frameworks/available)
  // is served by assessment-service. Without this entry, cloud-direct mode fell
  // through to the governance-service fallback (or, when that base URL was empty,
  // to a same-origin relative URL that returned the SPA index.html instead of
  // JSON) — so the wizard's framework picker rendered zero cards.
  [/^\/frameworks/, 'assessment'],
  [/^\/risks/, 'risk'],
  [/^\/compliance/, 'compliance'],
  [/^\/audits/, 'audit'],
  [/^\/dashboards/, 'dashboard'],
  [/^\/reports/, 'reporting'],
  [/^\/notifications/, 'notification'],
  [/^\/integrations/, 'integration'],
  [/^\/evidence/, 'evidence'],
  [/^\/analyze|^\/chat|^\/tasks/, 'aiOrchestrator'],
  // AI Document Intelligence pipeline → ai-orchestrator-service
  [/^\/doc-intel/, 'aiOrchestrator'],
  // RAG semantic-search / similar / analyze → knowledge-service (port 5122)
  [/^\/knowledge/, 'knowledge'],
  [/^\/settings/, 'settings'],
  // Feature Engine routes (client-facing)
  [/^\/features/, 'featureEngine'],
  [/^\/relationships/, 'featureEngine'],
  [/^\/contracts/, 'featureEngine'],
  [/^\/offers/, 'featureEngine'],
  // Client Management routes (client-facing)
  [/^\/clients/, 'clientManagement'],
  [/^\/dossiers/, 'clientManagement'],
  // Social Layer B — Business Calendar & Booking (client-management-service)
  [/^\/booking/, 'clientManagement'],
  // COLLAB-01: provider<->client per-engagement messaging threads (workspace-service)
  [/^\/collab/, 'workspace'],
  // SOCIAL-LAYER (Forum): business-owned discussion forums (workspace-service)
  [/^\/forum/, 'workspace'],
  // CCM: Continuous Controls Monitoring routes to governance-service
  [/^\/ccm/, 'governance'],
  // ── Scoring v2 / ML / EU-AI-Act / risk-pro escalation → governance-service ──
  // (the bare-path fallback already hits governance, listed for explicit intent)
  [/^\/scoring/, 'governance'],
  [/^\/ml($|\/)/, 'governance'],
  [/^\/eu-ai-act/, 'governance'],
  [/^\/escalation-rules/, 'governance'],
  // Auto-gov-IA #23: continuous AI-governance registry/classify/drift/scorecard → governance
  [/^\/ai-governance/, 'governance'],
  // Trust Center #28: authenticated trust-center control surface → governance
  [/^\/trust-center/, 'governance'],
  // Board Room #27: board meetings / packs / motions / minutes → governance
  [/^\/board-room/, 'governance'],
  // Signatures: reusable e-signature evidence for sign-off / approval surfaces → governance
  [/^\/signatures/, 'governance'],
  // AI authoring / copilot / draft assists → ai-orchestrator-service
  [/^\/ai\/(copilot|policy|draft)/, 'aiOrchestrator'],
]

// ── Path → service mapping for ALREADY-VERSIONED /api/v2/* paths ──────────────
// These endpoint constants already carry their full `/api/v2/...` prefix, so the
// resolver must NOT re-inject `/api/v1`. Primarily relevant in cloud-direct mode;
// in this (cloud-gateway) portal the nginx api-gateway owns the per-prefix routing.
const PATH_SERVICE_MAP_V2: Array<[RegExp, keyof typeof CLOUD_SERVICE_URLS]> = [
  [/^\/api\/v2\/billing/, 'billing'],
  [/^\/api\/v2\/iam/, 'user'],
  [/^\/api\/v2\/integrations/, 'integration'],
  [/^\/api\/v2\/connectors/, 'integration'],
  [/^\/api\/v2\/ingest/, 'integration'],
  // Persisted agents #22 → ai-orchestrator-service
  [/^\/api\/v2\/agents/, 'aiOrchestrator'],
  // Control-plane #26 → tenant-service (tenant lifecycle / FinOps / drift)
  [/^\/api\/v2\/tenant-lifecycle/, 'tenant'],
  [/^\/api\/v2\/finops/, 'tenant'],
  [/^\/api\/v2\/control-plane/, 'tenant'],
  // Certifications #29 → settings-service (platform certs + data-residency)
  [/^\/api\/v2\/platform/, 'settings'],
]

/**
 * Resolves the full URL for an API endpoint path based on the backend mode.
 *
 * - local:         Returns `/api{path}` (Vite proxy rewrites /api/ → /api/v1/)
 * - cloud-gateway: Returns `https://api-gateway.../api/v1{path}`
 * - cloud-direct:  Returns `https://{service}.../api/v1{path}`
 */
export function resolveApiUrl(path: string): string {
  // ── Already-versioned /api/v2/* (or any literal /api/* prefix) ──
  // These paths carry their own version segment; never double the /api prefix.
  const isVersionedPath = path.startsWith('/api/')

  if (BACKEND_MODE === 'local') {
    // Vite proxy intercepts /api/* and rewrites to /api/v1/*. A path that already
    // begins with /api (e.g. /api/v2/...) is passed through verbatim.
    return isVersionedPath ? path : `/api${path}`
  }

  if (BACKEND_MODE === 'cloud-gateway') {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || ''
    // baseUrl ends in `/api/v1`. A path that already carries its own version
    // segment (e.g. `/api/v2/iam/...` or a literal `/api/v1/...`) must hit the
    // gateway ORIGIN with the path verbatim — otherwise we get `/api/v1/api/v2/...`.
    if (isVersionedPath) {
      const origin = baseUrl.replace(/\/api\/v1\/?$/, '')
      return `${origin}${path}`
    }
    return `${baseUrl}${path}`
  }

  // cloud-direct: versioned /api/v2/* paths route straight to the owning service
  // host with the path verbatim (no /api/v1 injection).
  if (isVersionedPath) {
    for (const [pattern, serviceKey] of PATH_SERVICE_MAP_V2) {
      if (pattern.test(path)) {
        return `${CLOUD_SERVICE_URLS[serviceKey]}${path}`
      }
    }
    // Unknown /api/v2 path → governance-service, verbatim.
    return `${CLOUD_SERVICE_URLS.governance}${path}`
  }

  // cloud-direct: find the correct service URL for this path
  for (const [pattern, serviceKey] of PATH_SERVICE_MAP) {
    if (pattern.test(path)) {
      const serviceUrl = CLOUD_SERVICE_URLS[serviceKey]
      return `${serviceUrl}/api/v1${path}`
    }
  }

  // Fallback to governance-service
  return `${CLOUD_SERVICE_URLS.governance}/api/v1${path}`
}

// ── Base URL for Axios client ─────────────────────────────────────────────────
function getBaseUrl(): string {
  if (BACKEND_MODE === 'cloud-gateway') {
    return import.meta.env.VITE_API_BASE_URL || ''
  }
  if (BACKEND_MODE === 'cloud-direct') {
    // In direct mode, we use resolveApiUrl() per-request instead of a fixed baseURL
    return ''
  }
  // local mode: relative /api path, proxied by Vite
  return import.meta.env.VITE_API_BASE_URL || '/api'
}

export const API_CONFIG = {
  // Base URL - varies by backend mode
  baseUrl: getBaseUrl(),

  // Backend mode
  mode: BACKEND_MODE,

  // Individual service URLs (for direct access if needed)
  services: CLOUD_SERVICE_URLS,

  // API version
  version: 'v1',

  // Request timeout (ms)
  timeout: 30000,

  // Retry configuration
  retry: {
    maxRetries: 3,
    retryDelay: 1000,
    retryCondition: (error: any) => {
      return error.response?.status >= 500 || error.code === 'ECONNABORTED'
    },
  },
}

// Service endpoint paths
export const API_ENDPOINTS = {
  // Assessment Service
  assessment: {
    list: '/assessments',
    detail: (id: string) => `/assessments/${id}`,
    create: '/assessments',
    update: (id: string) => `/assessments/${id}`,
    delete: (id: string) => `/assessments/${id}`,
    questionnaire: (id: string) => `/assessments/${id}/questionnaire`,
    scoring: (id: string) => `/assessments/${id}/scoring`,
    gaps: (id: string) => `/assessments/${id}/gaps`,
  },

  // Risk Service
  risk: {
    list: '/risks',
    detail: (id: string) => `/risks/${id}`,
    create: '/risks',
    update: (id: string) => `/risks/${id}`,
    delete: (id: string) => `/risks/${id}`,
    summary: '/risks/summary',
    heatmap: '/risks/heatmap',
    treatments: (id: string) => `/risks/${id}/treatments`,
    aida: '/risks/aida-assessment',
    vendors: '/risks/vendors',
  },

  // Compliance Service
  compliance: {
    dashboard: '/compliance/dashboard',
    regulations: '/compliance/regulations',
    pia: '/compliance/pia',
    consent: '/compliance/consent',
    dsr: '/compliance/dsr',
    loi25: '/compliance/loi25/efvp',
    gdprDpia: '/compliance/gdpr/dpia',
    iso42001: '/compliance/iso42001/assessment',
    nistAi: '/compliance/nist-ai-rmf/assessment',
  },

  // Governance Service
  governance: {
    instances: '/governance-instances',
    meetings: '/meetings',
    agendaItems: (meetingId: string) => `/meetings/${meetingId}/agenda-items`,
    decisions: (meetingId: string) => `/meetings/${meetingId}/decisions`,
    actionItems: '/action-items',
    raciMatrices: '/raci-matrices',
    policies: '/policies',
    standards: '/standards',
    processes: '/processes',
    kpis: '/kpis',
  },

  // Board Room #27 (governance-service /api/v1/board-room/*)
  // Bare paths resolve to /api/v1/board-room/* via resolveApiUrl; the {id}
  // sub-resources are function forms. All route to governance-service.
  boardRoom: {
    meetings: '/board-room/meetings',
    meeting: (id: string) => `/board-room/meetings/${id}`,
    assemblePack: (id: string) => `/board-room/meetings/${id}/assemble-pack`,
    pack: (id: string) => `/board-room/meetings/${id}/pack`,
    motions: (id: string) => `/board-room/meetings/${id}/motions`,
    vote: (id: string, motionId: string) =>
      `/board-room/meetings/${id}/motions/${motionId}/vote`,
    minutes: (id: string) => `/board-room/meetings/${id}/minutes`,
    signMinutes: (id: string, minutesId: string) =>
      `/board-room/meetings/${id}/minutes/${minutesId}/sign`,
  },

  // Signatures — reusable hand-drawn / typed e-signature evidence
  // (governance-service /api/v1/signatures/*). Captured on any sign-off /
  // approval surface; append-only / immutable.
  signatures: {
    create: '/signatures',
    list: '/signatures',
    detail: (id: string) => `/signatures/${id}`,
  },

  // Strategy Service
  strategy: {
    visions: '/visions',
    objectives: '/objectives',
    businessCases: '/business-cases',
    roiCalculate: '/roi/calculate',
    roadmaps: '/roadmaps',
    prioritization: '/prioritization',
  },

  // Audit Service
  audit: {
    list: '/audits',
    detail: (id: string) => `/audits/${id}`,
    create: '/audits',
    checklist: (id: string) => `/audits/${id}/checklist`,
    findings: (id: string) => `/audits/${id}/findings`,
    actions: (findingId: string) => `/audits/findings/${findingId}/actions`,
    trail: '/audit-trail',
    templates: '/audits/templates',
  },

  // Evidence Service
  evidence: {
    upload: '/evidence',
    list: '/evidence',
    detail: (id: string) => `/evidence/${id}`,
    download: (id: string) => `/evidence/${id}/download`,
    search: '/evidence/search',
    expiring: '/evidence/expiring',
    quota: '/evidence/quota',
  },

  // Reporting Service
  reporting: {
    pdf: '/reports/pdf',
    pptx: '/reports/pptx',
    excel: '/reports/excel',
    list: '/reports',
    boardDeck: '/reports/board-deck',
    templates: '/templates',
    schedules: '/schedules',
    share: (id: string) => `/reports/${id}/share`,
    widgets: '/widgets',
  },

  // Notification Service
  notification: {
    send: '/notifications',
    batch: '/notifications/batch',
    list: '/notifications',
    templates: '/templates',
    preferences: (userId: string) => `/preferences/${userId}`,
    digests: '/digests',
    stats: '/stats',
    trackOpen: '/track/open',
    trackClick: '/track/click',
  },

  // Integration Service
  integration: {
    list: '/integrations',
    detail: (id: string) => `/integrations/${id}`,
    create: '/integrations',
    update: (id: string) => `/integrations/${id}`,
    delete: (id: string) => `/integrations/${id}`,
    test: (id: string) => `/integrations/${id}/test`,
    sync: (id: string) => `/integrations/${id}/sync`,
    webhooks: (id: string) => `/integrations/${id}/webhooks`,
    health: (id: string) => `/integrations/${id}/health`,
    users: (id: string) => `/integrations/${id}/users`,
    groups: (id: string) => `/integrations/${id}/groups`,
  },

  // Dashboard Service
  dashboard: {
    executive: '/dashboards/executive',
    cdao: '/dashboards/cdao',
    board: '/dashboards/board',
    cfo: '/dashboards/cfo',
    grc: '/dashboards/grc',
    kpis: '/dashboards/kpis',
    exportPdf: '/dashboards/export-pdf',
    trends: '/dashboards/trends',
  },

  // AI Orchestrator Service
  ai: {
    analyze: '/analyze',
    analyzeStream: '/analyze/stream',
    chat: '/chat',
    tasks: '/tasks',
  },

  // ISO 42001 AI Management System (via Assessment Service)
  iso42001: {
    // Framework data
    domains: '/assessments/domains?framework=ISO42001',
    dimensions: '/assessments/frameworks/ISO42001/dimensions',
    questions: '/assessments/frameworks/ISO42001/questions',

    // Templates
    templates: '/assessments/templates?framework=ISO42001',
    templateDetail: (id: string) => `/assessments/templates/${id}`,

    // Assessments
    assessments: '/assessments?framework=ISO42001',
    assessmentDetail: (id: string) => `/assessments/${id}`,

    // Responses
    responses: (assessmentId: string) => `/assessments/${assessmentId}/responses`,

    // Dimension scoring
    dimensionScores: (assessmentId: string) => `/assessments/${assessmentId}/dimension-scores`,
    dimensionAnalysis: (assessmentId: string) => `/assessments/${assessmentId}/dimension-analysis`,
    calculateDimensionScores: (assessmentId: string) => `/assessments/${assessmentId}/calculate-dimension-scores`,

    // Visualization
    radar: (assessmentId: string) => `/assessments/${assessmentId}/maturity-radar`,
    heatmap: (assessmentId: string) => `/assessments/${assessmentId}/maturity-heatmap`,
    maturitySummary: (assessmentId: string) => `/assessments/${assessmentId}/maturity-summary`,

    // Consolidation
    consolidate: (assessmentId: string) => `/assessments/${assessmentId}/consolidate`,
    consolidationResults: (assessmentId: string) => `/assessments/${assessmentId}/consolidation-results`,
    gapAnalysis: (assessmentId: string) => `/assessments/${assessmentId}/gap-analysis`,

    // Respondents
    respondents: (assessmentId: string) => `/assessments/${assessmentId}/respondents`,

    // Organizational units
    orgUnits: '/assessments/organizational-units',
  },

  // Generic Maturity Framework (via Assessment Service)
  maturity: {
    // Framework catalog
    frameworks: '/assessments/frameworks',
    frameworkDetail: (code: string) => `/assessments/frameworks/${code}`,
    domains: (code: string) => `/assessments/domains?framework=${code}`,
    dimensions: (code: string) => `/assessments/frameworks/${code}/dimensions`,
    questions: (code: string) => `/assessments/frameworks/${code}/questions`,

    // Templates
    templates: '/assessments/templates',
    templateDetail: (id: string) => `/assessments/templates/${id}`,

    // Assessments
    assessments: '/assessments',
    assessmentDetail: (id: string) => `/assessments/${id}`,

    // Responses
    responses: (assessmentId: string) => `/assessments/${assessmentId}/responses`,
    bulkResponses: (assessmentId: string) => `/assessments/${assessmentId}/responses/bulk`,

    // Scoring & Visualization
    dimensionScores: (assessmentId: string) => `/assessments/${assessmentId}/dimension-scores`,
    dimensionAnalysis: (assessmentId: string) => `/assessments/${assessmentId}/dimension-analysis`,
    radar: (assessmentId: string) => `/assessments/${assessmentId}/maturity-radar`,
    heatmap: (assessmentId: string) => `/assessments/${assessmentId}/maturity-heatmap`,
    summary: (assessmentId: string) => `/assessments/${assessmentId}/maturity-summary`,

    // Consolidation
    consolidate: (assessmentId: string) => `/assessments/${assessmentId}/consolidate`,
    consolidationResults: (assessmentId: string) => `/assessments/${assessmentId}/consolidation-results`,

    // Targets & Gap Analysis
    targets: (assessmentId: string) => `/assessments/${assessmentId}/targets`,
    gapAnalysis: (assessmentId: string) => `/assessments/${assessmentId}/gap-analysis`,
    calculateGaps: (assessmentId: string) => `/assessments/${assessmentId}/targets/calculate-gaps`,

    // Roadmap
    roadmapPhases: (assessmentId: string) => `/assessments/${assessmentId}/roadmap/phases`,
    roadmapItems: (assessmentId: string) => `/assessments/${assessmentId}/roadmap/items`,
    roadmapProgress: (assessmentId: string) => `/assessments/${assessmentId}/roadmap/progress`,
    roadmapAutoGenerate: (assessmentId: string) => `/assessments/${assessmentId}/roadmap/auto-generate`,

    // Evolution & Snapshots
    snapshots: (assessmentId: string) => `/assessments/${assessmentId}/snapshots`,
    evolution: (assessmentId: string) => `/assessments/${assessmentId}/evolution`,
    forecast: (assessmentId: string) => `/assessments/${assessmentId}/forecast`,

    // Benchmark
    benchmark: (assessmentId: string) => `/assessments/${assessmentId}/benchmark`,
    percentile: (assessmentId: string) => `/assessments/${assessmentId}/benchmark/percentile`,
    sectorBenchmark: (code: string, sector: string) => `/assessments/frameworks/${code}/benchmark/sector/${sector}`,
    benchmarkCohorts: '/assessments/benchmark/cohorts',

    // Respondents & Org
    respondents: (assessmentId: string) => `/assessments/${assessmentId}/respondents`,
    orgUnits: '/assessments/organizational-units',
  },

  // AuditPRO Service
  auditPro: {
    // Dashboard
    dashboard: '/audit-pro/dashboard',
    cockpit: '/audit-pro/cockpit',
    // Planning
    universe: '/audit-pro/universe',
    annualPlans: '/audit-pro/annual-plans',
    resources: '/audit-pro/resources',
    // Execution
    missions: '/audit-pro/missions',
    workpapers: '/audit-pro/workpapers',
    checklists: '/audit-pro/checklists',
    evidences: '/audit-pro/evidences',
    // Findings & Actions
    findings: '/audit-pro/findings',
    actions: '/audit-pro/actions',
    recommendations: '/audit-pro/recommendations',
    tracking: '/audit-pro/tracking',
    // Certification
    certReadiness: '/audit-pro/certification/readiness',
    certGaps: '/audit-pro/certification/gaps',
    certTimeline: '/audit-pro/certification/timeline',
    certControls: '/audit-pro/certification/controls',
    // Reports & Config
    reports: '/audit-pro/reports',
    committee: '/audit-pro/committee',
    selfAssessments: '/audit-pro/self-assessments',
    auditTrail: '/audit-pro/audit-trail',
    templates: '/audit-pro/templates',
    settings: '/audit-pro/settings',
    engagements: '/audit-pro/engagements',
    integrations: '/audit-pro/integrations',
    calendar: '/audit-pro/calendar',
  },

  // GovernancePRO Service
  governancePro: {
    // Hub & Dashboard
    hub: '/governance-pro/hub',
    cockpit: '/governance-pro/cockpit',

    // Domains
    domains: '/governance-pro/domains',
    domain: (id: string) => `/governance-pro/domains/${id}`,
    domainFramework: (domainId: string) => `/governance-pro/domains/${domainId}/framework`,

    // Frameworks
    frameworks: '/governance-pro/frameworks',
    framework: (id: string) => `/governance-pro/frameworks/${id}`,

    // Normative Documents
    normativeDocuments: '/governance-pro/normative-documents',
    normativeDocument: (id: string) => `/governance-pro/normative-documents/${id}`,

    // Models
    governanceModel: '/governance-pro/model',
    operatingModel: '/governance-pro/operating-model',

    // Roles
    domainRoles: '/governance-pro/roles',
    domainRolesByDomain: (domainId: string) => `/governance-pro/roles/by-domain/${domainId}`,

    // GRC
    grcDashboard: '/governance-pro/grc/dashboard',
    regulatoryRegister: '/governance-pro/grc/regulatory',
    regulatoryItem: (id: string) => `/governance-pro/grc/regulatory/${id}`,
    riskRegister: '/governance-pro/grc/risks',
    controlsAudit: '/governance-pro/grc/controls-audit',
    authorityRegister: '/governance-pro/grc/authority',

    // Analysis
    aiImpactAssessments: '/governance-pro/analysis/ai-impact',
    aiImpactAssessment: (id: string) => `/governance-pro/analysis/ai-impact/${id}`,
    aiSystemInventory: '/governance-pro/analysis/ai-systems',
    strategicAnalysis: '/governance-pro/analysis/strategic',
    maturityBridge: '/governance-pro/analysis/maturity-bridge',
    benchmarks: '/governance-pro/analysis/benchmarks',
    alignment: '/governance-pro/analysis/alignment',

    // Targets
    targets: '/governance-pro/targets',

    // Objectives
    objectives: '/governance-pro/objectives',
    createObjective: '/governance-pro/objectives',
    updateObjective: (id: string) => `/governance-pro/objectives/${id}`,
    deleteObjective: (id: string) => `/governance-pro/objectives/${id}`,

    // Programs
    programs: '/governance-pro/programs',
    program: (id: string) => `/governance-pro/programs/${id}`,

    // Processes & Ops
    governanceProcesses: '/governance-pro/processes',
    opsGovernance: '/governance-pro/ops-governance',
    opsException: (id: string) => `/governance-pro/ops-governance/exceptions/${id}`,
    riskProfile: '/governance-pro/risk-profile',

    // Culture
    culture: '/governance-pro/culture',

    // Roadmap
    roadmap: '/governance-pro/roadmap',

    // Reports & Templates
    reports: '/governance-pro/reports',
    report: (id: string) => `/governance-pro/reports/${id}`,
    downloadReport: (id: string) => `/governance-pro/reports/${id}/download`,
    templates: '/governance-pro/templates',
    template: (id: string) => `/governance-pro/templates/${id}`,

    // Configuration
    configuration: '/governance-pro/configuration',

    // Wizard
    wizardSave: '/governance-pro/wizard/save',
    wizardGenerate: '/governance-pro/wizard/generate',
    wizardSession: (sessionId: string) => `/governance-pro/wizard/${sessionId}`,
  },

  // SecurityPRO Service
  securityPro: {
    hub: '/security-pro/hub',
    cockpit: '/security-pro/cockpit',
    score: '/security-pro/score',
    posture: '/security-pro/posture',
    socDashboard: '/security-pro/soc/dashboard',
    socMetrics: '/security-pro/soc/metrics',
    incidents: '/security-pro/incidents',
    incident: (id: string) => `/security-pro/incidents/${id}`,
    createIncident: '/security-pro/incidents',
    updateIncident: (id: string) => `/security-pro/incidents/${id}`,
    deleteIncident: (id: string) => `/security-pro/incidents/${id}`,
    threatIntel: '/security-pro/threat-intel',
    createThreatIoc: '/security-pro/threat-intel/iocs',
    deleteThreatIoc: (id: string) => `/security-pro/threat-intel/iocs/${id}`,
    alerts: '/security-pro/alerts',
    createAlert: '/security-pro/alerts',
    updateAlert: (id: string) => `/security-pro/alerts/${id}`,
    deleteAlert: (id: string) => `/security-pro/alerts/${id}`,
    vulns: '/security-pro/vulns',
    createVuln: '/security-pro/vulns',
    vulnsStats: '/security-pro/vulns/stats',
    updateVuln: (id: string) => `/security-pro/vulns/${id}`,
    deleteVuln: (id: string) => `/security-pro/vulns/${id}`,
    scanVulns: '/security-pro/vulns/scan',
    importCve: '/security-pro/vulns/import',
    iam: '/security-pro/iam',
    controls: '/security-pro/controls',
    createControl: '/security-pro/controls',
    controlsStats: '/security-pro/controls/stats',
    updateControl: (id: string) => `/security-pro/controls/${id}`,
    deleteControl: (id: string) => `/security-pro/controls/${id}`,
    encryption: '/security-pro/encryption',
    bcp: '/security-pro/bcp',
    createBcp: '/security-pro/bcp',
    bcpDetail: (id: string) => `/security-pro/bcp/${id}`,
    updateBcp: (id: string) => `/security-pro/bcp/${id}`,
    deleteBcp: (id: string) => `/security-pro/bcp/${id}`,
    exercises: '/security-pro/exercises',
    createExercise: '/security-pro/exercises',
    updateExercise: (id: string) => `/security-pro/exercises/${id}`,
    deleteExercise: (id: string) => `/security-pro/exercises/${id}`,
    dr: '/security-pro/dr',
    createDrSite: '/security-pro/dr/sites',
    deleteDrSite: (id: string) => `/security-pro/dr/sites/${id}`,
    frameworks: '/security-pro/frameworks',
    frameworkDetail: (code: string) => `/security-pro/frameworks/${code}`,
    iso27001: '/security-pro/iso27001',
    soc2: '/security-pro/soc2',
    reportExec: '/security-pro/reports/executive',
    reportOps: '/security-pro/reports/operational',
    generateReport: '/security-pro/reports/generate',
    integrations: '/security-pro/integrations',
    updateIntegration: (id: string) => `/security-pro/integrations/${id}`,
    auditTrail: '/security-pro/audit-trail',
    auditTrailExport: '/security-pro/audit-trail/export',
    templates: '/security-pro/templates',
    createTemplate: '/security-pro/templates',
    updateTemplate: (id: string) => `/security-pro/templates/${id}`,
    deleteTemplate: (id: string) => `/security-pro/templates/${id}`,
    settings: '/security-pro/settings',
    updateSettings: '/security-pro/settings',
  },

  // RiskPRO Service
  riskPro: {
    hub: '/risk-pro/hub',
    cockpit: '/risk-pro/cockpit',
    matrix: '/risk-pro/matrix',
    appetite: '/risk-pro/appetite',
    risks: '/risk-pro/risks',
    risk: (id: string) => `/risk-pro/risks/${id}`,
    createRisk: '/risk-pro/risks',
    updateRisk: (id: string) => `/risk-pro/risks/${id}`,
    deleteRisk: (id: string) => `/risk-pro/risks/${id}`,
    scenarios: '/risk-pro/scenarios',
    createScenario: '/risk-pro/scenarios',
    updateScenario: (id: string) => `/risk-pro/scenarios/${id}`,
    deleteScenario: (id: string) => `/risk-pro/scenarios/${id}`,
    taxonomy: '/risk-pro/taxonomy',
    assessment: '/risk-pro/assessment',
    bowtie: (riskId: string) => `/risk-pro/bowtie/${riskId}`,
    createBowtie: '/risk-pro/bowtie',
    quantitative: '/risk-pro/quantitative',
    controls: '/risk-pro/controls',
    createControl: '/risk-pro/controls',
    updateControl: (id: string) => `/risk-pro/controls/${id}`,
    deleteControl: (id: string) => `/risk-pro/controls/${id}`,
    treatmentPlans: '/risk-pro/treatment-plans',
    createTreatmentPlan: '/risk-pro/treatment-plans',
    updateTreatmentPlan: (id: string) => `/risk-pro/treatment-plans/${id}`,
    deleteTreatmentPlan: (id: string) => `/risk-pro/treatment-plans/${id}`,
    threeLines: '/risk-pro/three-lines',
    kri: '/risk-pro/kri',
    createKri: '/risk-pro/kri',
    updateKri: (id: string) => `/risk-pro/kri/${id}`,
    deleteKri: (id: string) => `/risk-pro/kri/${id}`,
    incidents: '/risk-pro/incidents',
    createIncident: '/risk-pro/incidents',
    updateIncident: (id: string) => `/risk-pro/incidents/${id}`,
    deleteIncident: (id: string) => `/risk-pro/incidents/${id}`,
    monitoring: '/risk-pro/monitoring',
    reportBoard: '/risk-pro/report-board',
    generateBoardReport: '/risk-pro/report-board/generate',
    reports: '/risk-pro/reports',
    frameworks: '/risk-pro/frameworks',
    frameworkDetail: (id: string) => `/risk-pro/frameworks/${id}`,
    integrations: '/risk-pro/integrations',
    auditTrail: '/risk-pro/audit-trail',
    templates: '/risk-pro/templates',
    createTemplate: '/risk-pro/templates',
    settings: '/risk-pro/settings',
    updateSettings: '/risk-pro/settings',
  },

  // AI-IAGPRO Service
  aiIaPro: {
    // Hub & Dashboard
    hub: '/ai-ia-pro/hub',
    cockpit: '/ai-ia-pro/cockpit',
    score: '/ai-ia-pro/score',
    readiness: '/ai-ia-pro/readiness',

    // AI Model Registry
    models: '/ai-ia-pro/models',
    model: (id: string) => `/ai-ia-pro/models/${id}`,
    createModel: '/ai-ia-pro/models',
    lifecycle: '/ai-ia-pro/lifecycle',
    mlops: '/ai-ia-pro/mlops',
    performance: '/ai-ia-pro/performance',

    // GenAI Governance
    genaiPolicies: '/ai-ia-pro/genai/policies',
    genaiGuardrails: '/ai-ia-pro/genai/guardrails',
    shadowAI: '/ai-ia-pro/shadow-ai',
    reportShadowAI: '/ai-ia-pro/shadow-ai/report',
    promptGov: '/ai-ia-pro/prompt-governance',
    dataGov: '/ai-ia-pro/data-governance',

    // EU AI Act & Compliance
    euAIAct: '/ai-ia-pro/eu-ai-act',
    euAIActClassification: '/ai-ia-pro/eu-ai-act/classification',
    bias: '/ai-ia-pro/bias',
    biasMetrics: '/ai-ia-pro/bias/metrics',
    explainability: '/ai-ia-pro/explainability',
    impactAssessments: '/ai-ia-pro/impact-assessments',

    // Frameworks
    frameworks: '/ai-ia-pro/frameworks',
    frameworkDetail: (code: string) => `/ai-ia-pro/frameworks/${code}`,
    nistAI: '/ai-ia-pro/nist-ai',
    iso42001: '/ai-ia-pro/iso42001',

    // Reports & Config
    reportExec: '/ai-ia-pro/reports/executive',
    generateReport: '/ai-ia-pro/reports/generate',
    integrations: '/ai-ia-pro/integrations',
    auditTrail: '/ai-ia-pro/audit-trail',
    settings: '/ai-ia-pro/settings',
    updateSettings: '/ai-ia-pro/settings',

    // CRUD mutations
    updateModel: (id: string) => `/ai-ia-pro/models/${id}`,
    deleteModel: (id: string) => `/ai-ia-pro/models/${id}`,
    transitionLifecycle: '/ai-ia-pro/lifecycle/transition',
    triggerPipeline: '/ai-ia-pro/mlops/trigger',
    configureThreshold: '/ai-ia-pro/performance/thresholds',
    acknowledgeAlert: '/ai-ia-pro/performance/alerts/acknowledge',
    createGenAIPolicy: '/ai-ia-pro/genai/policies',
    updateGenAIPolicy: (id: string) => `/ai-ia-pro/genai/policies/${id}`,
    deleteGenAIPolicy: (id: string) => `/ai-ia-pro/genai/policies/${id}`,
    updateShadowAI: (id: string) => `/ai-ia-pro/shadow-ai/${id}`,
    createPromptTemplate: '/ai-ia-pro/prompt-governance/templates',
    createDataset: '/ai-ia-pro/data-governance/datasets',
    classifyEUAIAct: '/ai-ia-pro/eu-ai-act/classify',
    updateEUObligation: '/ai-ia-pro/eu-ai-act/obligation',
    launchBiasAudit: '/ai-ia-pro/bias/audit',
    updateBiasThresholds: '/ai-ia-pro/bias/thresholds',
    createModelCard: '/ai-ia-pro/explainability/model-cards',
    createImpactAssessment: '/ai-ia-pro/impact-assessments',
    updateImpactAssessment: (id: string) => `/ai-ia-pro/impact-assessments/${id}`,
    updateNistCategory: '/ai-ia-pro/nist-ai/category',
    updateIso42001Control: '/ai-ia-pro/iso42001/control',
    updateReadinessDimension: '/ai-ia-pro/readiness/dimension',
    exportReport: '/ai-ia-pro/reports/export',
    toggleIntegration: (mod: string) => `/ai-ia-pro/integrations/${mod}/toggle`,
    addTeamMember: '/ai-ia-pro/settings/team',
    removeTeamMember: (id: string) => `/ai-ia-pro/settings/team/${id}`,
    addOrganization: '/ai-ia-pro/settings/organizations',
    removeOrganization: (id: string) => `/ai-ia-pro/settings/organizations/${id}`,
    updateScoringWeights: '/ai-ia-pro/settings/scoring-weights',
    addTool: '/ai-ia-pro/settings/tools',
    removeTool: (id: string) => `/ai-ia-pro/settings/tools/${id}`,

    // Simulation Service (simulation-service router prefix = /api/v1/simulations).
    // These constants MUST be bare `/simulations/*` paths: resolveApiUrl injects the
    // `/api/v1` (cloud-gateway: `${base}/api/v1` + path; cloud-direct: `${simUrl}/api/v1`
    // + path). The former `/simulation-service/api/v1/simulations` prefix DOUBLE-STACKED
    // in cloud-gateway mode → `/api/v1/simulation-service/api/v1/simulations/*` 404
    // (deep-test QA residual, 2026-07-12). Bare paths resolve to the gateway-correct
    // `/api/v1/simulations/*` and, in cloud-direct, to `${simUrl}/api/v1/simulations/*`.
    simulations: '/simulations',
    simulation: (id: string) => `/simulations/${id}`,
    runSimulation: (id: string) => `/simulations/${id}/run`,
    simulationStatus: (id: string) => `/simulations/${id}/status`,
    simulationResults: (id: string) => `/simulations/${id}/results`,
    cloneSimulation: (id: string) => `/simulations/${id}/clone`,
    approveSimulation: (id: string) => `/simulations/${id}/approve`,
    tagSimulation: (id: string) => `/simulations/${id}/tag`,
    simulationHub: '/simulations/hub',
    compareSimulations: '/simulations/compare',
    comparison: (id: string) => `/simulations/compare/${id}`,
    forecasts: '/simulations/forecast',
    forecast: (id: string) => `/simulations/forecast/${id}`,
    multiScenarioForecast: '/simulations/forecast/multi-scenario',
    simulationTemplates: '/simulations/templates',
    applyTemplate: (id: string) => `/simulations/templates/${id}/apply`,
    sensitivity: (simId: string) => `/simulations/${simId}/sensitivity`,
    tornado: (simId: string) => `/simulations/${simId}/sensitivity/tornado`,

    // Simulation page-level data (replacing hardcoded)
    simulationLabStats: '/ai-ia-pro/simulation-lab/stats',
    simulationLabByType: '/ai-ia-pro/simulation-lab/by-type',
    simulationLabThroughput: '/ai-ia-pro/simulation-lab/throughput',
    simulationLabRecent: '/ai-ia-pro/simulation-lab/recent',
    scenarioCatalog: '/ai-ia-pro/scenario-simulation/catalog',
    scenarioSimStats: '/ai-ia-pro/scenario-simulation/stats',
    whatIfHistory: '/ai-ia-pro/what-if-analysis/history',
    whatIfStats: '/ai-ia-pro/what-if-analysis/stats',
    governanceSimMetrics: '/ai-ia-pro/governance-simulation/metrics',
    governanceSimScenarios: '/ai-ia-pro/governance-simulation/scenarios',
    governanceStressTests: '/ai-ia-pro/governance-simulation/stress-tests',

    // AIOps Runbooks
    aiopsRunbooks: '/ai-ia-pro/aiops/runbooks',
    aiopsRunbook: (id: string) => `/ai-ia-pro/aiops/runbooks/${id}`,
    aiopsExecutions: '/ai-ia-pro/aiops/executions',
    aiopsStats: '/ai-ia-pro/aiops/stats',
    aiopsMetrics: '/ai-ia-pro/aiops/metrics',

    // ML/CV endpoints
    mlModels: '/ai-ia-pro/ml/models',
    mlModel: (id: string) => `/ai-ia-pro/ml/models/${id}`,
    mlTrainingDashboard: '/ai-ia-pro/ml/training/dashboard',
    mlPredictions: '/ai-ia-pro/ml/predictions',
    mlDocumentIntelligence: '/ai-ia-pro/ml/document-intelligence',
  },

  // ChangePRO Service
  changePro: {
    // Hub & Dashboard
    hub: '/change-pro/hub',
    cockpit: '/change-pro/cockpit',
    score: '/change-pro/score',
    maturite: '/change-pro/maturite',

    // Modeles & Cadres
    adkar: '/change-pro/adkar',
    adkarProject: (id: string) => `/change-pro/adkar/${id}`,
    updateAdkar: (id: string) => `/change-pro/adkar/${id}`,
    kotter: '/change-pro/kotter',
    updateKotterStep: '/change-pro/kotter/steps',
    createQuickWin: '/change-pro/kotter/quick-wins',
    lewinBridges: '/change-pro/lewin-bridges',
    createForce: '/change-pro/lewin-bridges/forces',
    createAlignmentAction: '/change-pro/mckinsey-7s/actions',
    mckinsey7s: '/change-pro/mckinsey-7s',
    evaluateMaturite: '/change-pro/maturite/evaluate',
    evaluateReadiness: '/change-pro/readiness/evaluate',
    evaluateCulture: '/change-pro/culture/evaluate',
    evaluateImpact: '/change-pro/impact/evaluate',
    createResistanceFactor: '/change-pro/resistance/factors',
    createCommunication: '/change-pro/communication/events',
    createInitiative: '/change-pro/roadmap/initiatives',
    createTrainingProgram: '/change-pro/formation/programs',
    launchEnpsSurvey: '/change-pro/enps/survey',
    recordAdoption: '/change-pro/adoption/record',
    calculateRoi: '/change-pro/roi/calculate',

    // Transformation
    roadmap: '/change-pro/roadmap',
    adoption: '/change-pro/adoption',
    resistance: '/change-pro/resistance',
    impact: '/change-pro/impact',

    // Communication & Culture
    communication: '/change-pro/communication',
    champions: '/change-pro/champions',
    createChampion: '/change-pro/champions',
    culture: '/change-pro/culture',
    enps: '/change-pro/enps',

    // Projets & Suivi
    projects: '/change-pro/projects',
    createProject: '/change-pro/projects',
    updateProject: (id: string) => `/change-pro/projects/${id}`,
    readiness: '/change-pro/readiness',
    formation: '/change-pro/formation',
    roi: '/change-pro/roi',

    // Rapports & Config
    reportExec: '/change-pro/report-exec',
    generateReport: '/change-pro/report-exec/generate',
    reports: '/change-pro/reports',
    frameworks: '/change-pro/frameworks',
    frameworkDetail: (id: string) => `/change-pro/frameworks/${id}`,
    integrations: '/change-pro/integrations',
    auditTrail: '/change-pro/audit-trail',
    templates: '/change-pro/templates',
    createTemplate: '/change-pro/templates',
    settings: '/change-pro/settings',
    updateSettings: '/change-pro/settings',
  },

  // DataPRO Service
  dataPro: {
    // Hub & Dashboard
    hub: '/data-pro/hub',
    cockpit: '/data-pro/cockpit',
    score: '/data-pro/score',
    maturite: '/data-pro/maturite',

    // DAMA DMBOK2 11 Knowledge Areas
    damaScores: '/data-pro/dama-scores',
    damaScore: (kaCode: string) => `/data-pro/dama-scores/${kaCode}`,
    damaRadar: '/data-pro/dama-scores/radar',
    damaGaps: '/data-pro/dama-scores/gaps',
    damaInitialize: '/data-pro/dama-scores/initialize',
    damaBulkUpdate: '/data-pro/dama-scores/bulk-update',

    // DCAM v3 8 Components
    dcam: '/data-pro/dcam',
    dcamSummary: '/data-pro/dcam/summary',
    dcamComponent: (code: string) => `/data-pro/dcam/${code}`,

    // CDMC 14 Key Controls
    cdmc: '/data-pro/cdmc',
    cdmcSummary: '/data-pro/cdmc/summary',
    cdmcGaps: '/data-pro/cdmc/gaps',
    cdmcThemes: '/data-pro/cdmc/themes',
    cdmcControl: (code: string) => `/data-pro/cdmc/${code}`,

    // Data Assets Inventory
    assets: '/data-pro/assets',
    asset: (id: string) => `/data-pro/assets/${id}`,

    // Data Quality 6 Dimensions
    quality: '/data-pro/quality',
    qualityOverview: '/data-pro/quality/overview',
    qualityAsset: (assetId: string) => `/data-pro/quality/asset/${assetId}`,
    qualityScore: (id: string) => `/data-pro/quality/${id}`,

    // Pipelines Registry
    pipelines: '/data-pro/pipelines',
    pipelinesHealth: '/data-pro/pipelines/health',
    pipeline: (id: string) => `/data-pro/pipelines/${id}`,

    // Gouvernance & Policies
    governance: '/data-pro/governance',
    createGovernancePolicy: '/data-pro/governance/policies',
    mdm: '/data-pro/mdm',
    metadata: '/data-pro/metadata',
    createMetadataAsset: '/data-pro/metadata/assets',

    // Architecture & Donnees
    architecture: '/data-pro/architecture',
    modeling: '/data-pro/modeling',
    storage: '/data-pro/storage',

    // FIX-06 (2026-06-07): real write paths for the previously read-only data-pro
    // modules. POST/GET/PUT/DELETE /data-pro/module-entries/{module}
    // (module = architecture|modeling|storage|security|dwh-bi|lifecycle|
    //  frameworks|dcam|cdmc|maturity-models). Created entries round-trip into the
    // owning module's GET response.
    moduleEntries: (module: string) => `/data-pro/module-entries/${module}`,
    moduleEntry: (module: string, id: string) => `/data-pro/module-entries/${module}/${id}`,

    // Securite & Contenu
    security: '/data-pro/security',
    dwhBi: '/data-pro/dwh-bi',
    documents: '/data-pro/documents',
    lifecycle: '/data-pro/lifecycle',

    // Cadres & Conformite
    frameworks: '/data-pro/frameworks',
    frameworkDetail: (id: string) => `/data-pro/frameworks/${id}`,
    maturityModels: '/data-pro/maturity-models',

    // Reports
    reports: '/data-pro/reports',
    reportExec: '/data-pro/reports/executive',
    generateReport: '/data-pro/reports/generate',
    report: (id: string) => `/data-pro/reports/${id}`,

    // Config & Admin
    integrations: '/data-pro/integrations',
    auditTrail: '/data-pro/audit-trail',
    settings: '/data-pro/settings',
    updateSettings: '/data-pro/settings',
  },

  // CompliancePRO Service
  compliancePro: {
    // Hub & Dashboard
    hub: '/compliance-pro/hub',
    cockpit: '/compliance-pro/cockpit',
    score: '/compliance-pro/score',
    posture: '/compliance-pro/posture',

    // RGPD & Privacy
    rgpd: '/compliance-pro/rgpd',
    rgpdArticles: '/compliance-pro/rgpd/articles',
    pia: '/compliance-pro/pia',
    createPia: '/compliance-pro/pia',
    updatePia: (id: string) => `/compliance-pro/pia/${id}`,
    dsr: '/compliance-pro/dsr',
    createDsr: '/compliance-pro/dsr',
    updateDsr: (id: string) => `/compliance-pro/dsr/${id}`,
    consent: '/compliance-pro/consent',
    createConsent: '/compliance-pro/consent',

    // Reglementations
    loi25: '/compliance-pro/loi25',
    ccpa: '/compliance-pro/ccpa',
    sox: '/compliance-pro/sox',
    soxControls: '/compliance-pro/sox/controls',
    pciDss: '/compliance-pro/pci-dss',

    // Gestion & Suivi
    obligations: '/compliance-pro/obligations',
    updateObligation: (id: string) => `/compliance-pro/obligations/${id}`,
    gaps: '/compliance-pro/gaps',
    updateGap: (id: string) => `/compliance-pro/gaps/${id}`,
    gapAnalysis: '/compliance-pro/gaps/analysis',
    actions: '/compliance-pro/actions',
    createAction: '/compliance-pro/actions',
    updateAction: (id: string) => `/compliance-pro/actions/${id}`,
    transfers: '/compliance-pro/transfers',
    createTransfer: '/compliance-pro/transfers',
    updateTransfer: (id: string) => `/compliance-pro/transfers/${id}`,

    // Cadres & Certification
    frameworks: '/compliance-pro/frameworks',
    frameworkDetail: (code: string) => `/compliance-pro/frameworks/${code}`,
    iso27701: '/compliance-pro/iso27701',
    nistPrivacy: '/compliance-pro/nist-privacy',
    certifications: '/compliance-pro/certifications',
    createCertification: '/compliance-pro/certifications',

    // Controls
    controls: '/compliance-pro/controls',
    createControl: '/compliance-pro/controls',
    updateControl: (id: string) => `/compliance-pro/controls/${id}`,
    deleteControl: (id: string) => `/compliance-pro/controls/${id}`,

    // Rapports & Config
    reportExec: '/compliance-pro/reports/executive',
    reportOps: '/compliance-pro/reports/operational',
    generateReport: '/compliance-pro/reports/generate',
    integrations: '/compliance-pro/integrations',
    auditTrail: '/compliance-pro/audit-trail',
    settings: '/compliance-pro/settings',
    updateSettings: '/compliance-pro/settings',
  },

  // Speak-Up / Whistleblowing (W2-01, EU Directive 2019/1937)
  // Backend router prefix: /compliance-pro/speak-up (services/governance-service/src/routers/speak_up.py:53)
  speakUp: {
    channels: '/compliance-pro/speak-up/channels',
    reports: '/compliance-pro/speak-up/reports',
    sla: '/compliance-pro/speak-up/sla',
  },

  // VendorPRO Service
  vendorPro: {
    // Hub & Dashboard
    hub: '/vendor-pro/hub',
    cockpit: '/vendor-pro/cockpit',
    score: '/vendor-pro/score',
    maturity: '/vendor-pro/maturity',

    // TPRM & Due Diligence
    tprm: '/vendor-pro/tprm',
    dueDiligence: '/vendor-pro/due-diligence',
    createDueDiligence: '/vendor-pro/due-diligence',
    concentration: '/vendor-pro/concentration',
    subprocessors: '/vendor-pro/subprocessors',

    // Contrats & Couts
    contracts: '/vendor-pro/contracts',
    contract: (id: string) => `/vendor-pro/contracts/${id}`,
    createContract: '/vendor-pro/contracts',
    sla: '/vendor-pro/sla',
    renewals: '/vendor-pro/renewals',
    costs: '/vendor-pro/costs',

    // Performance & Evaluation
    scorecards: '/vendor-pro/scorecards',
    scorecard: (vendorId: string) => `/vendor-pro/scorecards/${vendorId}`,
    performance: '/vendor-pro/performance',
    catalogue: '/vendor-pro/catalogue',
    createVendor: '/vendor-pro/catalogue',
    benchmarks: '/vendor-pro/benchmarks',

    // Conformite & Audit
    rgpd: '/vendor-pro/rgpd',
    clauses: '/vendor-pro/clauses',
    vendorAudit: '/vendor-pro/vendor-audit',
    vendorAuditDetail: (id: string) => `/vendor-pro/vendor-audit/${id}`,
    createVendorAudit: '/vendor-pro/vendor-audit',
    certifications: '/vendor-pro/certifications',

    // Rapports & Config
    reportExec: '/vendor-pro/report-exec',
    generateReport: '/vendor-pro/report-exec/generate',
    frameworks: '/vendor-pro/frameworks',
    frameworkDetail: (code: string) => `/vendor-pro/frameworks/${code}`,
    integrations: '/vendor-pro/integrations',
    auditTrail: '/vendor-pro/audit-trail',
    templates: '/vendor-pro/templates',
    createTemplate: '/vendor-pro/templates',
    settings: '/vendor-pro/settings',
    updateSettings: '/vendor-pro/settings',
  },

  // DashboardPRO Service
  dashboardPro: {
    // Cockpit C-Level
    hub: '/dashboard-pro/hub',
    score: '/dashboard-pro/score',
    alertes: '/dashboard-pro/alertes',
    createAlerte: '/dashboard-pro/alertes',
    csuite: '/dashboard-pro/csuite',

    // Cross-Modules
    panorama: '/dashboard-pro/panorama',
    drilldown: '/dashboard-pro/drilldown',
    drilldownModule: (moduleId: string) => `/dashboard-pro/drilldown/${moduleId}`,
    correlations: '/dashboard-pro/correlations',
    heatmap: '/dashboard-pro/heatmap',

    // Scoring & Maturite
    scoreConsolide: '/dashboard-pro/score-consolide',
    maturite: '/dashboard-pro/maturite',
    risqueAgrege: '/dashboard-pro/risque-agrege',
    conformiteUnifiee: '/dashboard-pro/conformite',

    // Rapports & CA
    rapportCA: '/dashboard-pro/rapport-ca',
    tableauCA: '/dashboard-pro/tableau-ca',
    kpis: '/dashboard-pro/kpis',
    tendances: '/dashboard-pro/tendances',

    // Multi-Org
    multiSite: '/dashboard-pro/multi-site',
    comparaison: '/dashboard-pro/comparaison',
    deploiement: '/dashboard-pro/deploiement',
    benchmarks: '/dashboard-pro/benchmarks',

    // Admin & Config
    config: '/dashboard-pro/config',
    updateConfig: '/dashboard-pro/config',
    integrations: '/dashboard-pro/integrations',
    auditTrail: '/dashboard-pro/audit-trail',
    settings: '/dashboard-pro/settings',
    updateSettings: '/dashboard-pro/settings',

    // Actions
    recalculate: '/dashboard-pro/recalculate',
    reportExec: '/dashboard-pro/reports/executive',
    generateReport: '/dashboard-pro/reports/generate',
  },

  // ── EthicsPRO ──────────────────────────────────────────────────────────────
  ethicsPro: {
    // S1 — Vue d'ensemble
    hub: '/ethics-pro/hub',
    cockpit: '/ethics-pro/cockpit',
    score: '/ethics-pro/score',
    maturity: '/ethics-pro/maturity',
    maturite: '/ethics-pro/maturity',

    // S2 — Politiques & Code
    politiques: '/ethics-pro/politiques',
    codeConduite: '/ethics-pro/code-conduite',
    comiteEthique: '/ethics-pro/comite-ethique',
    createComiteReunion: '/ethics-pro/comite-ethique/reunions',
    formation: '/ethics-pro/formation',
    createFormation: '/ethics-pro/formation',
    createPolitique: '/ethics-pro/politiques',
    createViolation: '/ethics-pro/code-conduite/violations',

    // S3 — Signalement & Conformite
    signalements: '/ethics-pro/signalements',
    signalement: (id: string) => `/ethics-pro/signalements/${id}`,
    createSignalement: '/ethics-pro/signalements',
    alerte: '/ethics-pro/alerte',
    conflitInteret: '/ethics-pro/conflit-interet',
    createConflitInteret: '/ethics-pro/conflit-interet',
    createAlerte: '/ethics-pro/alerte',
    antiCorruption: '/ethics-pro/anti-corruption',
    createCadeau: '/ethics-pro/anti-corruption/cadeaux',

    // S4 — Culture & ESG
    culture: '/ethics-pro/culture',
    enquetes: '/ethics-pro/enquetes',
    createEnquete: '/ethics-pro/enquetes',
    createSondage: '/ethics-pro/culture/sondages',
    diversite: '/ethics-pro/diversite',
    createProgrammeDiversite: '/ethics-pro/diversite/programmes',
    esg: '/ethics-pro/esg',

    // S5 — Cadres & Certification
    frameworks: '/ethics-pro/frameworks',
    frameworkDetail: (code: string) => `/ethics-pro/frameworks/${code}`,
    iso37001: '/ethics-pro/iso37001',
    createIsoConstat: '/ethics-pro/iso37001/constats',
    sapinII: '/ethics-pro/sapin-ii',
    certification: '/ethics-pro/certification',

    // S6 — Rapports & Config
    reportExec: '/ethics-pro/report-exec',
    generateReport: '/ethics-pro/report-exec/generate',
    integrations: '/ethics-pro/integrations',
    auditTrail: '/ethics-pro/audit-trail',
    templates: '/ethics-pro/templates',
    settings: '/ethics-pro/settings',
    updateSettings: '/ethics-pro/settings',
  },

  // ── MaturityPRO ─────────────────────────────────────────────────────────────
  maturityPro: {
    // S1 — Vue d'ensemble
    hub: '/maturity-pro/hub',
    cockpit: '/maturity-pro/cockpit',
    score: '/maturity-pro/score',
    maturity: '/maturity-pro/maturity',

    // S2 — Modeles & Frameworks
    cmmi: '/maturity-pro/cmmi',
    cobit: '/maturity-pro/cobit',
    iso33001: '/maturity-pro/iso33001',
    customModel: '/maturity-pro/custom-model',
    createCustomModel: '/maturity-pro/custom-model',

    // S3 — Evaluation & Assessment
    campaigns: '/maturity-pro/campaigns',
    campaign: (id: string) => `/maturity-pro/campaigns/${id}`,
    createCampaign: '/maturity-pro/campaigns',
    dimensions: '/maturity-pro/dimensions',
    questionnaires: '/maturity-pro/questionnaires',
    questionnaireQuestions: (id: string) => `/maturity-pro/questionnaires/${id}/questions`,
    questionnaireResponses: (id: string) => `/maturity-pro/questionnaires/${id}/responses`,
    benchmarking: '/maturity-pro/benchmarking',

    // S4 — Analyse & Progression
    gapAnalyse: '/maturity-pro/gap-analyse',
    roadmap: '/maturity-pro/roadmap',
    progression: '/maturity-pro/progression',
    objectifs: '/maturity-pro/objectifs',

    // S5 — Cadres & Certification
    frameworks: '/maturity-pro/frameworks',
    frameworkDetail: (code: string) => `/maturity-pro/frameworks/${code}`,
    mapping: '/maturity-pro/mapping',
    certification: '/maturity-pro/certification',
    benchmarks: '/maturity-pro/benchmarks',

    // S6 — Rapports & Config
    reportExec: '/maturity-pro/report-exec',
    generateReport: '/maturity-pro/report-exec/generate',
    integrations: '/maturity-pro/integrations',
    auditTrail: '/maturity-pro/audit-trail',
    templates: '/maturity-pro/templates',
    settings: '/maturity-pro/settings',
    updateSettings: '/maturity-pro/settings',
  },

  // ── StrategyPRO ─────────────────────────────────────────────────────────────
  strategyPro: {
    // S1 — Vue d'ensemble
    hub: '/strategy-pro/hub',
    cockpit: '/strategy-pro/cockpit',
    score: '/strategy-pro/score',
    vision: '/strategy-pro/vision',

    // S2 — OKR & Objectifs
    okrDashboard: '/strategy-pro/okr',
    objectifs: '/strategy-pro/objectifs',
    objectif: (id: string) => `/strategy-pro/objectifs/${id}`,
    createObjectif: '/strategy-pro/objectifs',
    updateObjectif: (id: string) => `/strategy-pro/objectifs/${id}`,
    deleteObjectif: (id: string) => `/strategy-pro/objectifs/${id}`,
    keyResults: '/strategy-pro/key-results',
    createKeyResult: '/strategy-pro/key-results',
    updateKeyResult: (id: string) => `/strategy-pro/key-results/${id}`,
    deleteKeyResult: (id: string) => `/strategy-pro/key-results/${id}`,
    alignement: '/strategy-pro/alignement',

    // S3 — BSC & Perspectives
    bscDashboard: '/strategy-pro/bsc',
    perspectives: '/strategy-pro/perspectives',
    strategicMap: '/strategy-pro/strategic-map',
    createStrategicMapLink: '/strategy-pro/strategic-map',
    deleteStrategicMapLink: (id: string) => `/strategy-pro/strategic-map/${id}`,
    initiatives: '/strategy-pro/initiatives',
    createInitiative: '/strategy-pro/initiatives',
    updateInitiative: (id: string) => `/strategy-pro/initiatives/${id}`,
    deleteInitiative: (id: string) => `/strategy-pro/initiatives/${id}`,
    milestones: (id: string) => `/strategy-pro/initiatives/${id}/milestones`,
    createMilestone: (id: string) => `/strategy-pro/initiatives/${id}/milestones`,

    // S4 — Planification & Analyse
    swot: '/strategy-pro/swot',
    createSwotItem: '/strategy-pro/swot/items',
    updateSwotItem: (id: string) => `/strategy-pro/swot/items/${id}`,
    deleteSwotItem: (id: string) => `/strategy-pro/swot/items/${id}`,
    createSwotAction: '/strategy-pro/swot/actions',
    deleteSwotAction: (id: string) => `/strategy-pro/swot/actions/${id}`,
    pestel: '/strategy-pro/pestel',
    createPestelFactor: '/strategy-pro/pestel',
    updatePestelFactor: (id: string) => `/strategy-pro/pestel/${id}`,
    deletePestelFactor: (id: string) => `/strategy-pro/pestel/${id}`,
    businessCase: '/strategy-pro/business-case',
    createBusinessCase: '/strategy-pro/business-case',
    updateBusinessCase: (id: string) => `/strategy-pro/business-case/${id}`,
    deleteBusinessCase: (id: string) => `/strategy-pro/business-case/${id}`,
    roi: '/strategy-pro/roi',

    // S5 — Cadres & Portfolio
    frameworks: '/strategy-pro/frameworks',
    frameworkDetail: (code: string) => `/strategy-pro/frameworks/${code}`,
    portfolio: '/strategy-pro/portfolio',
    programs: '/strategy-pro/programs',
    createProgram: '/strategy-pro/programs',
    updateProgram: (id: string) => `/strategy-pro/programs/${id}`,
    deleteProgram: (id: string) => `/strategy-pro/programs/${id}`,
    governance: '/strategy-pro/governance',
    createCommittee: '/strategy-pro/governance/committees',
    updateCommittee: (id: string) => `/strategy-pro/governance/committees/${id}`,
    deleteCommittee: (id: string) => `/strategy-pro/governance/committees/${id}`,
    createDecision: '/strategy-pro/governance/decisions',

    // S6 — Rapports & Config
    reportExec: '/strategy-pro/report-exec',
    generateReport: '/strategy-pro/report-exec/generate',
    integrations: '/strategy-pro/integrations',
    auditTrail: '/strategy-pro/audit-trail',
    templates: '/strategy-pro/templates',
    settings: '/strategy-pro/settings',
    updateSettings: '/strategy-pro/settings',
    seed: '/strategy-pro/seed',
  },

  // ── BusinessModelPRO ─────────────────────────────────────────────────────────
  businessModelPro: {
    // S1 — Vue d'ensemble
    hub: '/business-model-pro/hub',
    cockpit: '/business-model-pro/cockpit',
    score: '/business-model-pro/score',
    maturite: '/business-model-pro/maturite',

    // S2 — Canevas & Creation
    canvases: '/business-model-pro/canvases',
    canvas: (id: string) => `/business-model-pro/canvases/${id}`,
    createCanvas: '/business-model-pro/canvases',
    updateCanvas: (id: string) => `/business-model-pro/canvases/${id}`,
    deleteCanvas: (id: string) => `/business-model-pro/canvases/${id}`,
    canvasZones: (id: string) => `/business-model-pro/canvases/${id}/zones`,
    createCard: (id: string) => `/business-model-pro/canvases/${id}/cards`,
    updateCard: (canvasId: string, cardId: string) => `/business-model-pro/canvases/${canvasId}/cards/${cardId}`,
    deleteCard: (canvasId: string, cardId: string) => `/business-model-pro/canvases/${canvasId}/cards/${cardId}`,

    // S3 — Collaboration
    sessions: '/business-model-pro/sessions',
    session: (id: string) => `/business-model-pro/sessions/${id}`,
    createSession: '/business-model-pro/sessions',
    updateSession: (id: string) => `/business-model-pro/sessions/${id}`,
    participants: (id: string) => `/business-model-pro/sessions/${id}/participants`,
    joinSession: (id: string) => `/business-model-pro/sessions/${id}/join`,
    deleteSession: (id: string) => `/business-model-pro/sessions/${id}`,
    contributions: '/business-model-pro/contributions',
    submitContribution: '/business-model-pro/contributions',
    deleteContribution: (id: string) => `/business-model-pro/contributions/${id}`,

    // S4 — Analyse & Alignement
    alignments: (canvasId: string) => `/business-model-pro/alignments/${canvasId}`,
    createAlignment: '/business-model-pro/alignments',
    impactAnalysis: (canvasId: string) => `/business-model-pro/impact-analysis/${canvasId}`,
    guidedQuestions: '/business-model-pro/guided-questions',
    submitAnswer: '/business-model-pro/guided-questions/answers',
    benchmarking: '/business-model-pro/benchmarking',

    // S5 — Cadres & Referentiels
    frameworks: '/business-model-pro/frameworks',
    framework: (id: string) => `/business-model-pro/frameworks/${id}`,
    templates: '/business-model-pro/templates',
    template: (id: string) => `/business-model-pro/templates/${id}`,
    createTemplate: '/business-model-pro/templates',
    leanCanvasConfig: '/business-model-pro/lean-canvas-config',

    // S6 — Rapports & Config
    reportExec: '/business-model-pro/reports/executive',
    exportReport: '/business-model-pro/reports/export',
    integrationsStrategy: '/business-model-pro/integrations/strategy',
    integrationsGovernance: '/business-model-pro/integrations/governance',
    integrationsRisk: '/business-model-pro/integrations/risk',
    integrationsCompliance: '/business-model-pro/integrations/compliance',
    auditTrail: '/business-model-pro/audit-trail',
    settings: '/business-model-pro/settings',
    updateSettings: '/business-model-pro/settings',
    outils: '/business-model-pro/outils',
  },

  // ── WorkspacePRO ─────────────────────────────────────────────────────────────
  workspacePro: {
    // S1 — Vue d'ensemble
    hub: '/workspace-pro/hub',
    workspaces: '/workspace-pro/workspaces',
    workspace: (id: string) => `/workspace-pro/workspaces/${id}`,
    createWorkspace: '/workspace-pro/workspaces',
    updateWorkspace: (id: string) => `/workspace-pro/workspaces/${id}`,
    deleteWorkspace: (id: string) => `/workspace-pro/workspaces/${id}`,
    activity: '/workspace-pro/activity',

    // S2 — Espace de Travail
    documents: (wsId: string) => `/workspace-pro/workspaces/${wsId}/documents`,
    document: (wsId: string, docId: string) => `/workspace-pro/workspaces/${wsId}/documents/${docId}`,
    uploadDocument: (wsId: string) => `/workspace-pro/workspaces/${wsId}/documents`,
    deleteDocument: (wsId: string, docId: string) => `/workspace-pro/workspaces/${wsId}/documents/${docId}`,
    reindexDocument: (wsId: string, docId: string) => `/workspace-pro/workspaces/${wsId}/documents/${docId}/reindex`,
    documentStats: (wsId: string) => `/workspace-pro/workspaces/${wsId}/documents/stats`,

    // S3 — Chat & RAG
    chatSessions: (wsId: string) => `/workspace-pro/workspaces/${wsId}/chat/sessions`,
    createChatSession: (wsId: string) => `/workspace-pro/workspaces/${wsId}/chat/sessions`,
    chatSession: (wsId: string, sessionId: string) => `/workspace-pro/workspaces/${wsId}/chat/sessions/${sessionId}`,
    sendMessage: (wsId: string, sessionId: string) => `/workspace-pro/workspaces/${wsId}/chat/sessions/${sessionId}/messages`,
    deleteChatSession: (wsId: string, sessionId: string) => `/workspace-pro/workspaces/${wsId}/chat/sessions/${sessionId}`,
    ragSearch: (wsId: string) => `/workspace-pro/workspaces/${wsId}/rag/search`,
    ragStatus: (wsId: string) => `/workspace-pro/workspaces/${wsId}/rag/status`,

    // S4 — Collaboration
    questions: (wsId: string) => `/workspace-pro/workspaces/${wsId}/questions`,
    question: (wsId: string, qId: string) => `/workspace-pro/workspaces/${wsId}/questions/${qId}`,
    createQuestion: (wsId: string) => `/workspace-pro/workspaces/${wsId}/questions`,
    postAnswer: (wsId: string, qId: string) => `/workspace-pro/workspaces/${wsId}/questions/${qId}/answers`,
    vote: '/workspace-pro/votes',
    themes: (wsId: string) => `/workspace-pro/workspaces/${wsId}/themes`,
    createTheme: (wsId: string) => `/workspace-pro/workspaces/${wsId}/themes`,
    updateTheme: (wsId: string, themeId: string) => `/workspace-pro/workspaces/${wsId}/themes/${themeId}`,
    deleteTheme: (wsId: string, themeId: string) => `/workspace-pro/workspaces/${wsId}/themes/${themeId}`,

    // S5 — Members & Invitations
    members: (wsId: string) => `/workspace-pro/workspaces/${wsId}/members`,
    addMember: (wsId: string) => `/workspace-pro/workspaces/${wsId}/members`,
    updateMember: (wsId: string, memberId: string) => `/workspace-pro/workspaces/${wsId}/members/${memberId}`,
    removeMember: (wsId: string, memberId: string) => `/workspace-pro/workspaces/${wsId}/members/${memberId}`,
    invitations: (wsId: string) => `/workspace-pro/workspaces/${wsId}/invitations`,
    sendInvitation: (wsId: string) => `/workspace-pro/workspaces/${wsId}/invitations`,
    acceptInvitation: (invId: string) => `/workspace-pro/invitations/${invId}/accept`,
    revokeInvitation: (wsId: string, invId: string) => `/workspace-pro/workspaces/${wsId}/invitations/${invId}`,

    // S6 — Tools & Profiles
    tools: (wsId: string) => `/workspace-pro/workspaces/${wsId}/tools`,
    createTool: (wsId: string) => `/workspace-pro/workspaces/${wsId}/tools`,
    updateTool: (wsId: string, toolId: string) => `/workspace-pro/workspaces/${wsId}/tools/${toolId}`,
    deleteTool: (wsId: string, toolId: string) => `/workspace-pro/workspaces/${wsId}/tools/${toolId}`,
    profile: (wsId: string) => `/workspace-pro/workspaces/${wsId}/profile`,
    updateProfile: (wsId: string) => `/workspace-pro/workspaces/${wsId}/profile`,

    // S7 — Config & Admin
    settings: (wsId: string) => `/workspace-pro/workspaces/${wsId}/settings`,
    updateSettings: (wsId: string) => `/workspace-pro/workspaces/${wsId}/settings`,
    webAccess: (wsId: string) => `/workspace-pro/workspaces/${wsId}/web-access`,
    updateWebAccess: (wsId: string) => `/workspace-pro/workspaces/${wsId}/web-access`,
    permissions: (wsId: string) => `/workspace-pro/workspaces/${wsId}/permissions`,
    reports: '/workspace-pro/reports',
    auditTrail: '/workspace-pro/audit-trail',
    integrations: '/workspace-pro/integration',

    // Copilot — personal tasks
    myComplianceTasks: '/workspace-pro/copilot/my-compliance-tasks',
    smartTaskPriority: '/workspace-pro/copilot/smart-task-priority',
    createTask: '/workspace-pro/copilot/tasks',
  },

  // ── COLLAB-01: Provider<->Client Messaging (workspace-service) ──────────────
  collab: {
    threads: '/collab/threads',
    thread: (id: string) => `/collab/threads/${id}`,
    createThread: '/collab/threads',
    messages: (id: string) => `/collab/threads/${id}/messages`,
    postMessage: (id: string) => `/collab/threads/${id}/messages`,
    markRead: (id: string) => `/collab/threads/${id}/mark-read`,
    // SOCIAL-LAYER D — multimedia + presence + participants
    media: (id: string) => `/collab/threads/${id}/media`,
    typing: (id: string) => `/collab/threads/${id}/typing`,
    participants: (id: string) => `/collab/threads/${id}/participants`,
  },

  // ── Unified Scoring v2 (governance-service) ─────────────────────────────────
  // Cross-domain scoring overview + per-domain breakdown + per-entity SHAP-style
  // contribution explanation. domain = risk|compliance|security|maturity|ai|...
  scoring: {
    overview: '/scoring/overview',
    domain: (domain: string) => `/scoring/${domain}`,
    explain: (domain: string, entityId: string) => `/scoring/${domain}/explain/${entityId}`,
  },

  // ── Evidence v2 — immutable record / versions / verify / seal / legal-hold ──
  // (evidence-service) chain-of-custody, content-hash verification, WORM seal.
  evidenceV2: {
    record: '/evidence/v2/record',
    versions: (id: string) => `/evidence/v2/record/${id}/versions`,
    verify: (id: string) => `/evidence/v2/record/${id}/verify`,
    seal: (id: string) => `/evidence/v2/record/${id}/seal`,
    legalHold: (id: string) => `/evidence/v2/record/${id}/legal-hold`,
  },

  // ── Billing v2 — unified credits / forecast / golden-rule / seats / quotas ──
  // (billing-service) AI-credit unification, spend forecast, golden-rule guard,
  // per-seat usage + entitlement check, per-role quota config.
  billingV2: {
    creditsUnified: '/api/v2/billing/credits/unified',
    creditsForecast: '/api/v2/billing/credits/v2/forecast',
    goldenRule: '/credits/v2/golden-rule',
    seatsUsage: '/api/v2/billing/v2/seats/usage',
    seatsCheck: '/api/v2/billing/v2/seats/check',
    roleQuotas: '/api/v2/billing/role-quotas',
  },

  // ── Subscriptions v2 (billing-service) — plan catalog + plan migration ──────
  subscriptionsV2: {
    plans: '/subscriptions/v2/plans',
    migratePlan: '/subscriptions/v2/migrate-plan',
  },

  // ── RiskPRO v2 — graph / escalation-rules / reconcile / suggest ─────────────
  // (governance-service) risk dependency graph, auto-escalation rule engine,
  // dual-register reconciliation, AI-suggested risks.
  riskProV2: {
    riskGraph: (id: string) => `/risk-pro/risks/${id}/graph`,
    escalationRules: '/escalation-rules',
    reconcile: '/risk-pro/risks/reconcile',
    suggest: '/risk-pro/risks/suggest',
  },

  // ── IAM v2 (user-service) — permissions / SoD / role-assignment / reviews ───
  // segregation-of-duties rules + violations, access reviews, SCIM tokens.
  iamV2: {
    permissions: '/api/v2/iam/permissions',
    sodRules: '/api/v2/iam/sod/rules',
    sodViolations: '/api/v2/iam/sod/violations',
    userRoles: (id: string) => `/api/v2/iam/users/${id}/roles`,
    accessReviews: '/api/v2/iam/access-reviews',
    scimTokens: '/api/v2/iam/scim-tokens',
  },

  // ── ML registry + per-prediction explanation (governance-service) ───────────
  // ML model registry; explain a single prediction (rt = run/record type, rid = id).
  ml: {
    registry: '/ml/registry',
    explain: (rt: string, rid: string) => `/ml/explain/${rt}/${rid}`,
  },

  // ── ML inference v2 (governance-service) — synchronous predict ──────────────
  mlInference: {
    predict: '/ml/v2/predict',
  },

  // ── AI Copilot status-narrative (ai-orchestrator) — credit-metered LLM ──────
  aiCopilot: {
    statusNarrative: '/ai/copilot/status-narrative',
  },

  // ── EU AI Act classify-assist (governance-service) — LLM-assisted classify ──
  euAiAct: {
    classifyAssist: '/eu-ai-act/classify-assist',
  },

  // ── AI policy authoring assists (ai-orchestrator) — tldr / quiz / qa ────────
  aiPolicy: {
    tldr: '/ai/policy/tldr',
    quiz: '/ai/policy/quiz',
    qa: '/ai/policy/qa',
  },

  // ── AI draft generators (ai-orchestrator) — policy / privacy-notice / model-card ──
  aiDraft: {
    policy: '/ai/draft/policy',
    privacyNotice: '/ai/draft/privacy-notice',
    modelCard: '/ai/draft/model-card',
  },

  // ── Connectors v2 (integration-service) — live-connector lifecycle ──────────
  // test/collect/findings/drift/schedule per integration + marketplace catalog.
  connectorsV2: {
    test: (id: string) => `/api/v2/integrations/${id}/test`,
    collect: (id: string) => `/api/v2/integrations/${id}/collect`,
    findings: (id: string) => `/api/v2/integrations/${id}/findings`,
    drift: (id: string) => `/api/v2/integrations/${id}/drift`,
    schedule: (id: string) => `/api/v2/integrations/${id}/schedule`,
    catalog: '/api/v2/connectors/catalog',
  },

  // ── DSPM #19 (governance-service) — Data Security Posture Management ─────────
  // Cloud data-store inventory, scan trigger, findings register + lineage graph.
  // Reachable at client route /data-pro/dspm (Data & IA context).
  dspm: {
    sources: '/data-pro/dspm/sources',
    scan: '/data-pro/dspm/scan',
    findings: '/data-pro/dspm/findings',
    lineage: '/data-pro/dspm/lineage',
    classifyFinding: (id: string) => `/data-pro/dspm/findings/${id}/classify`,
  },

  // ── PSA #20 (governance-service) — Professional Services Automation ─────────
  // Versioned /psa/v2/* surface: rate-cards, time-entries, capacity, etc.
  // The axios interceptor + resolveApiUrl pass the /api/v2 prefix through verbatim.
  psa: {
    rateCards: '/psa/v2/rate-cards',
    timeEntries: '/psa/v2/time-entries',
    capacity: '/psa/v2/capacity',
    utilization: '/psa/v2/utilization',
    realization: '/psa/v2/realization',
    portfolio: '/psa/v2/portfolio',
    bench: '/psa/v2/bench',
    capacityVsPipeline: '/psa/v2/capacity-vs-pipeline',
    engagementEconomics: (mandatId: string) => `/psa/v2/engagement-economics/${mandatId}`,
  },

  // ── Persisted Agents #22 (ai-orchestrator) — versioned /api/v2/agents/* ──────
  // Persistent agent registry + guardrails + catalog; run-now + per-agent runs.
  // Reachable at client route /admin/agents (Administration context).
  persistedAgents: {
    persistent: '/api/v2/agents/persistent',
    guardrails: '/api/v2/agents/guardrails',
    catalog: '/api/v2/agents/catalog',
    runNow: (id: string) => `/api/v2/agents/persistent/${id}/run-now`,
    runs: (id: string) => `/api/v2/agents/persistent/${id}/runs`,
  },

  // ── Monte-Carlo #24 (governance-service) — risk-pro quantitative simulation ──
  // Simulate / operational-risk capital / what-if / scenarios / runs history.
  // Reachable at client route /risk-pro/montecarlo (Risk context).
  montecarlo: {
    simulate: '/risk-pro/montecarlo/simulate',
    opriskCapital: '/risk-pro/montecarlo/oprisk-capital',
    whatIf: '/risk-pro/montecarlo/what-if',
    scenarios: '/risk-pro/montecarlo/scenarios',
    runs: '/risk-pro/montecarlo/runs',
  },

  // ── Control-plane #26 (tenant-service) — versioned /api/v2/* ─────────────────
  // Tenant lifecycle actions + per-tenant status, FinOps usage, config drift.
  // Reachable at client route /admin/control-plane (Administration context).
  controlPlane: {
    tenantLifecycleAction: (action: string) => `/api/v2/tenant-lifecycle/${action}`,
    tenantLifecycleStatus: (tenantId: string) => `/api/v2/tenant-lifecycle/${tenantId}/status`,
    finopsUsage: '/api/v2/finops/usage',
    drift: '/api/v2/control-plane/drift',
  },

  // ── TPRM #21 (governance-service, under /api/v1/vendor-pro) ──────────────────
  // Continuous security-ratings sync + nth-party (supply-chain) graph, propagation,
  // concentration, dependency cycles + portfolio rescore + ratings subscriptions.
  // Reachable at client route /vendor-pro/nth-party (Risk + Vendor contexts).
  tprm: {
    ratingsSync: '/vendor-pro/ratings/sync',
    ratingsHistory: (vendor: string) => `/vendor-pro/ratings/${vendor}/history`,
    nthPartySync: '/vendor-pro/nth-party/sync',
    nthPartyGraph: '/vendor-pro/nth-party/graph/v2',
    nthPartyPropagation: '/vendor-pro/nth-party/propagation',
    nthPartyConcentration: '/vendor-pro/nth-party/concentration',
    nthPartyCycles: '/vendor-pro/nth-party/cycles',
    vendorRescore: '/vendor-pro/vendor-rescore',
    subscriptions: '/vendor-pro/tprm/subscriptions',
  },

  // ── Auto-gov-IA #23 (governance-service) ────────────────────────────────────
  // Continuous AI-governance registry + copilot classification, self-drift watch,
  // governance scorecard + per-system model card.
  // Reachable at client route /ai-ia-pro/ai-governance (Data & IA context).
  aiGovernance: {
    registry: '/ai-governance/registry',
    classifyCopilots: '/ai-governance/classify-copilots',
    selfDrift: '/ai-governance/self-drift',
    scorecard: '/ai-governance/scorecard',
    modelCard: (id: string) => `/ai-governance/model-card/${id}`,
  },

  // ── Trust Center #28 (governance-service) ───────────────────────────────────
  // Authenticated control-surface for the public Trust Center: config, live status,
  // regulatory watch feed + AI-governance scorecard embed.
  // Reachable at client route /admin/trust-center (Administration context).
  trustCenter: {
    config: '/trust-center/config',
    liveStatus: '/trust-center/live-status',
    regWatch: '/trust-center/reg-watch',
    aiGovernanceScorecard: '/trust-center/ai-governance-scorecard',
  },

  // ── Certifications #29 (settings-service) — versioned /api/v2/platform/* ─────
  // Platform-level certification register + data-residency config (per-tenant).
  // The axios interceptor + resolveApiUrl pass the /api/v2 prefix through verbatim.
  // Reachable at client route /admin/certifications (Administration context).
  platformCerts: {
    certifications: '/api/v2/platform/certifications',
    dataResidency: '/api/v2/platform/data-residency',
    dataResidencyByTenant: (tenantId: string) => `/api/v2/platform/data-residency/${tenantId}`,
  },
}
